import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import Alert from '@material-ui/lab/Alert';
import McpKeyService from '../../services/mcp_key.service';

const useStyles = makeStyles((theme) => ({
  section: { marginTop: theme.spacing(3), maxWidth: 720 },
  note: { color: '#888', marginTop: theme.spacing(1) },
  keyText: { fontFamily: 'monospace', wordBreak: 'break-all' },
  newKeyBox: { background: '#f5f2ea', border: '1px solid #ccc', padding: theme.spacing(1), borderRadius: 4, marginTop: 8 },
  help: { background: '#faf9f5', border: '1px solid #e0dccf', padding: theme.spacing(2), borderRadius: 4, marginTop: 8 },
  mono: { fontFamily: 'monospace', background: '#eee', padding: '1px 4px', borderRadius: 3, wordBreak: 'break-all' },
  step: { marginTop: theme.spacing(1) }
}));

const copy = (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
  } else {
    const ta = document.createElement('textarea');
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand('copy'); } catch (e) { /* ignore */ }
    document.body.removeChild(ta);
  }
};

const fmt = d => (d ? new Date(d).toLocaleString() : '—');

const McpKeysSection = () => {
  const classes = useStyles();
  const [keys, setKeys] = useState([]);
  const [revealed, setRevealed] = useState({}); // id -> full key
  const [message, setMessage] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [newKey, setNewKey] = useState(null); // {label, key} shown once
  const [mcpUrl, setMcpUrl] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  const apiErr = err => err.response?.data?.message || 'Request failed.';

  const load = () => {
    McpKeyService.list()
      .then(res => setKeys(res.data))
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  useEffect(() => {
    load();
    McpKeyService.connection().then(res => setMcpUrl(res.data.url || '')).catch(() => {});
  }, []);

  const doGenerate = () => {
    McpKeyService.generate(label.trim() || 'Unnamed key')
      .then(res => {
        setNewKey({ label: res.data.label, key: res.data.key });
        setDialogOpen(false);
        setLabel('');
        load();
      })
      .catch(err => { setMessage({ severity: 'error', text: apiErr(err) }); setDialogOpen(false); });
  };

  const doReveal = (id) => {
    McpKeyService.reveal(id)
      .then(res => setRevealed(r => ({ ...r, [id]: res.data.key })))
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  const doDelete = (row) => {
    if (!window.confirm(`Revoke key "${row.label}"? Any client using it will stop working.`)) return;
    McpKeyService.remove(row.id)
      .then(() => { setMessage({ severity: 'success', text: 'Key revoked.' }); load(); })
      .catch(err => setMessage({ severity: 'error', text: apiErr(err) }));
  };

  return (
    <div className={classes.section}>
      <Divider />
      <Typography variant="subtitle1" style={{ marginTop: 16 }}>MCP API keys</Typography>
      <Typography variant="caption" className={classes.note} component="div">
        Keys let an MCP client (e.g. Claude Desktop via MetaMCP, or Home Assistant) act on this
        household. Send the key as <code>Authorization: Bearer &lt;key&gt;</code>.
      </Typography>

      {message && <Alert severity={message.severity} style={{ margin: '8px 0' }}>{message.text}</Alert>}

      {newKey &&
        <div className={classes.newKeyBox}>
          <Typography variant="body2"><strong>New key "{newKey.label}"</strong> — copy it now:</Typography>
          <div className={classes.keyText}>{newKey.key}</div>
          <Button size="small" startIcon={<FileCopyIcon />} onClick={() => copy(newKey.key)}>Copy</Button>
          <Button size="small" onClick={() => setNewKey(null)}>Dismiss</Button>
        </div>}

      <Table size="small" style={{ marginTop: 12 }}>
        <TableHead>
          <TableRow>
            <TableCell>Label</TableCell>
            <TableCell>Key</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Last used</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.length === 0 &&
            <TableRow><TableCell colSpan={5}><em>No keys yet.</em></TableCell></TableRow>}
          {keys.map(k => (
            <TableRow key={k.id}>
              <TableCell>{k.label}</TableCell>
              <TableCell className={classes.keyText}>
                {revealed[k.id] ? revealed[k.id] : k.prefix + '…'}
              </TableCell>
              <TableCell>{fmt(k.createdAt)}</TableCell>
              <TableCell>{fmt(k.lastUsedAt)}</TableCell>
              <TableCell align="right">
                <Tooltip title="Reveal key">
                  <IconButton size="small" onClick={() => doReveal(k.id)}><VisibilityIcon fontSize="small" /></IconButton>
                </Tooltip>
                {revealed[k.id] &&
                  <Tooltip title="Copy key">
                    <IconButton size="small" onClick={() => copy(revealed[k.id])}><FileCopyIcon fontSize="small" /></IconButton>
                  </Tooltip>}
                <Tooltip title="Revoke key">
                  <IconButton size="small" onClick={() => doDelete(k)}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Button color="primary" variant="contained" size="small" style={{ marginTop: 12 }}
        onClick={() => setDialogOpen(true)}>Generate key</Button>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Generate MCP key</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Label (e.g. Freezer phone)" value={label}
            onChange={e => setLabel(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="secondary">Cancel</Button>
          <Button onClick={doGenerate} color="primary">Generate</Button>
        </DialogActions>
      </Dialog>

      <div style={{ marginTop: 16 }}>
        <Link component="button" variant="body2" onClick={() => setShowHelp(!showHelp)}>
          {showHelp ? '▾ Hide' : '▸ How to connect a client (MetaMCP)'}
        </Link>
        <Collapse in={showHelp}>
          <div className={classes.help}>
            <Typography variant="body2">
              This app's MCP server speaks <strong>Streamable HTTP</strong>. Route it through
              MetaMCP, which re-exposes it to Claude Desktop and Home Assistant.
            </Typography>

            <Typography variant="body2" className={classes.step}>
              <strong>1. Add an MCP Server in MetaMCP</strong> (Servers → New):
            </Typography>
            <ul>
              <li>Type / transport: <strong>Streamable HTTP</strong></li>
              <li>URL:{' '}
                {mcpUrl
                  ? <span className={classes.mono}>{mcpUrl}</span>
                  : <em>not set — an admin must set “MCP public URL” in Settings → Admin → Config (MCP).</em>}
              </li>
              <li>Request header: <span className={classes.mono}>Authorization: Bearer &lt;your key above&gt;</span></li>
            </ul>

            <Typography variant="body2" className={classes.step}>
              <strong>2. Namespace &amp; Endpoint:</strong> add the server to a Namespace, then create an
              Endpoint for that namespace (Streamable HTTP for Claude Desktop; also SSE if you'll use
              Home Assistant).
            </Typography>

            <Typography variant="body2" className={classes.step}>
              <strong>3. Claude Desktop</strong> (stdio-only) connects to the MetaMCP endpoint via
              <span className={classes.mono}>mcp-proxy</span> using your MetaMCP API key (<span className={classes.mono}>sk_mt_…</span>):
            </Typography>
            <pre className={classes.mono} style={{ whiteSpace: 'pre-wrap', padding: 8 }}>{`{
  "mcpServers": {
    "Food": {
      "command": "uvx",
      "args": ["mcp-proxy", "--transport", "streamablehttp",
               "https://<metamcp-host>/metamcp/<endpoint>/mcp"],
      "env": { "API_ACCESS_TOKEN": "sk_mt_..." }
    }
  }
}`}</pre>

            <Typography variant="body2" className={classes.step}>
              <strong>4. Home Assistant</strong> (MCP Client integration is SSE-only): point it at the
              MetaMCP <strong>SSE</strong> endpoint, then use an Ollama conversation agent with a
              tool-calling model to speak stock updates.
            </Typography>
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default McpKeysSection;
